import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import api from "../../api/axios";
import { Search, Save, User, Calendar, Edit2, CheckCircle2, AlertCircle } from "lucide-react";

const AdminLeaveSettings = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryEmployeeId = searchParams.get("employee");

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);
    const [processingAction, setProcessingAction] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Global settings state
    const [payCycleStart, setPayCycleStart] = useState(1);
    const [savingCycle, setSavingCycle] = useState(false);

    useEffect(() => {
        fetchData(true);
    }, []);

    const fetchData = async (isInitial = false) => {
        try {
            const [empRes, settingsRes] = await Promise.all([
                api.get("/api/admin/employees/all?includeEscalations=false"),
                api.get("/api/settings").catch(() => null)
            ]);
            
            // Auto-filter if employee ID was passed from the Verify button
            if (isInitial && queryEmployeeId) {
                const target = empRes.data.find(e => e._id === queryEmployeeId);
                if (target) {
                    setSearch(target.name);
                    if (searchParams.get("leaveId")) {
                        setPendingAction({
                            employeeId: target._id,
                            employeeName: target.name,
                            leaveId: searchParams.get("leaveId"),
                            leaveType: searchParams.get("leaveType"),
                            leaveDays: searchParams.get("leaveDays"),
                            normalDays: searchParams.get("normalDays"),
                            lopDays: searchParams.get("lopDays"),
                        });
                    }
                }
                // Clear the URL so refreshing doesn't keep the filter
                setSearchParams({}, { replace: true });
            }
            
            setEmployees(empRes.data || []);
            if (settingsRes?.data?.payCycle?.startDay) {
                setPayCycleStart(settingsRes.data.payCycle.startDay);
            }
        } catch (err) {
            console.error("Failed to load leave settings data", err);
        } finally {
            setLoading(false);
        }
    };

    const handleLeaveAction = async (status) => {
        if (!pendingAction) return;
        
        let payload = { status };
        
        if (status === 'rejected') {
            const reason = window.prompt("Please provide a reason for rejecting this leave application:");
            if (!reason || !reason.trim()) {
                alert("A rejection reason is required to reject a leave application.");
                return;
            }
            payload.rejectionReason = reason.trim();
        }
        
        setProcessingAction(true);
        try {
            await api.patch(`/api/admin/leave/${pendingAction.leaveId}`, payload);
            alert(`Leave request successfully ${status}!`);
            setPendingAction(null);
            setSearchParams({}, { replace: true });
            fetchData(); // Refetch to get updated balances
        } catch (err) {
            alert(`Failed to ${status} leave: ` + (err.response?.data?.message || err.message));
        } finally {
            setProcessingAction(false);
        }
    };

    const updateEmployeeLeave = async (id, field, value) => {
        setSaving(id);
        try {
            await api.patch(`/api/admin/employees/${id}`, { [field]: Number(value) || 0 });
            setEmployees(prev => prev.map(emp => emp._id === id ? { ...emp, [field]: Number(value) || 0 } : emp));
        } catch (err) {
            alert("Failed to update balance");
        } finally {
            setSaving(null);
        }
    };

    const updatePayCycle = async () => {
        setSavingCycle(true);
        try {
            await api.post("/api/admin/settings", { key: 'payCycle', value: { startDay: Number(payCycleStart), endDay: Number(payCycleStart) } });
            alert("Leave Allocation Cycle updated successfully!");
        } catch (err) {
            alert("Failed to update cycle");
        } finally {
            setSavingCycle(false);
        }
    };

    const filteredEmployees = employees.filter(emp => 
        (emp.name || "").toLowerCase().includes(search.toLowerCase()) || 
        (emp.employeeId || "").toLowerCase().includes(search.toLowerCase())
    );

    const effectivePageSize = pageSize === "all" ? 999999 : Number(pageSize);
    const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / effectivePageSize));
    const indexOfLastItem = currentPage * effectivePageSize;
    const indexOfFirstItem = indexOfLastItem - effectivePageSize;
    const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);

    // Reset to page 1 when search or page size changes
    useEffect(() => {
        setCurrentPage(1);
    }, [search, pageSize]);

    return (
        <AdminLayout>
            <div className="flex-1 h-full p-4 sm:p-6 lg:p-8 w-full space-y-6 overflow-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[var(--text-primary)]">Leave Balances & Settings</h1>
                        <p className="mt-1 text-sm font-medium text-[var(--text-secondary)]">Manage employee leave quotas and global allocation cycle.</p>
                    </div>
                </div>

                {pendingAction && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div>
                            <h3 className="font-black text-amber-600 dark:text-amber-400">Pending Leave Action</h3>
                            <p className="text-sm font-semibold text-[var(--text-secondary)] mt-1">
                                <span className="font-bold text-[var(--text-primary)]">{pendingAction.employeeName}</span> has requested <span className="font-bold text-[var(--text-primary)]">{pendingAction.leaveDays} Days</span> of <span className="uppercase font-bold tracking-wider">{pendingAction.leaveType?.replace('-', ' ')}</span>.
                            </p>
                            {Number(pendingAction.lopDays) > 0 && (
                                <p className="text-xs font-bold text-red-500 mt-1.5 flex items-center gap-1.5 bg-red-500/10 w-fit px-2.5 py-1 rounded-md">
                                    <AlertCircle size={14} />
                                    Split: {pendingAction.normalDays} normal days + {pendingAction.lopDays} LOP days (Insufficient balance)
                                </p>
                            )}
                            <p className="text-xs font-semibold text-[var(--text-secondary)] mt-1">Please verify their balance in the table below and approve or reject.</p>
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto shrink-0">
                            <button 
                                onClick={() => handleLeaveAction('rejected')}
                                disabled={processingAction}
                                className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl border border-red-500/30 bg-red-500/10 text-red-600 font-bold text-sm hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
                            >
                                Reject
                            </button>
                            <button 
                                onClick={() => handleLeaveAction('approved')}
                                disabled={processingAction}
                                className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl bg-emerald-500 text-white font-bold text-sm shadow-md shadow-emerald-500/20 hover:bg-emerald-600 transition-all disabled:opacity-50"
                            >
                                {processingAction ? 'Processing...' : 'Approve Leave'}
                            </button>
                        </div>
                    </div>
                )}

                <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl p-5 sm:p-6 shadow-sm flex flex-col sm:flex-row items-center gap-4">
                    <div className="flex-1">
                        <h2 className="text-base font-bold text-[var(--text-primary)] flex items-center gap-2">
                            <Calendar size={18} className="text-[var(--accent-primary)]" />
                            Monthly Allocation Date
                        </h2>
                        <p className="text-xs text-[var(--text-secondary)] mt-1 font-medium">On which day of the month should the automated leave accrual script run?</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <select 
                            value={payCycleStart} 
                            onChange={(e) => setPayCycleStart(e.target.value)}
                            className="h-10 px-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] outline-none text-sm font-bold"
                        >
                            {[...Array(28)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </select>
                        <button 
                            onClick={updatePayCycle}
                            disabled={savingCycle}
                            className="h-10 px-4 rounded-xl bg-[var(--accent-primary)] text-white text-xs font-black uppercase tracking-wider hover:bg-[var(--accent-primary)]/90 transition-all disabled:opacity-50"
                        >
                            {savingCycle ? "Saving..." : "Save Date"}
                        </button>
                    </div>
                </div>

                <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-sm overflow-hidden flex flex-col">
                    <div className="p-4 sm:p-5 border-b border-[var(--border-color)] flex flex-col sm:flex-row gap-4 justify-between items-center bg-[var(--bg-secondary)]/30">
                        <h3 className="font-bold text-[var(--text-primary)] text-sm uppercase tracking-wider">Employee Balances</h3>
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input 
                                type="text"
                                placeholder="Search employee..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 h-10 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] text-sm font-medium outline-none focus:border-[var(--accent-primary)]"
                            />
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1000px] text-sm text-left">
                            <thead className="bg-[var(--bg-secondary)]/50 text-[10px] uppercase font-black tracking-wider text-[var(--text-secondary)]">
                                <tr>
                                    <th className="px-5 py-3">Employee</th>
                                    <th className="px-5 py-3 text-center border-l border-[var(--border-color)]/30">Casual Leave</th>
                                    <th className="px-5 py-3 text-center border-l border-[var(--border-color)]/30">Medical Leave</th>
                                    <th className="px-5 py-3 text-center border-l border-[var(--border-color)]/30">Comp Off</th>
                                    <th className="px-5 py-3 text-center border-l border-[var(--border-color)]/30">Loss of Pay (LOP)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--border-color)]">
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="px-5 py-10 text-center text-[var(--text-secondary)]">Loading balances...</td>
                                    </tr>
                                ) : currentEmployees.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-5 py-10 text-center text-[var(--text-secondary)]">No employees found.</td>
                                    </tr>
                                ) : (
                                    currentEmployees.map(emp => (
                                        <tr key={emp._id} className={`hover:bg-[var(--bg-secondary)]/20 transition-colors ${queryEmployeeId === emp._id ? 'bg-[var(--accent-primary)]/5' : ''}`}>
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center font-bold text-xs shrink-0">
                                                        {emp.name?.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-[var(--text-primary)]">{emp.name}</p>
                                                        <p className="text-[10px] font-semibold text-[var(--text-secondary)]">{emp.role?.toUpperCase()}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Casual Leave */}
                                            <td className="px-5 py-3 border-l border-[var(--border-color)]/30 bg-emerald-500/5">
                                                <div className="flex items-center justify-center gap-4">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase">Rate/Mo</span>
                                                        <input type="number" className="w-12 h-8 px-1 text-center rounded-lg border border-emerald-500/20 bg-white dark:bg-black/20 font-bold text-xs text-emerald-700 dark:text-emerald-300 outline-none focus:border-emerald-500"
                                                            value={emp.monthlyCasualLeave || 0}
                                                            onBlur={(e) => updateEmployeeLeave(emp._id, 'monthlyCasualLeave', e.target.value)}
                                                            onChange={(e) => setEmployees(prev => prev.map(p => p._id === emp._id ? {...p, monthlyCasualLeave: e.target.value} : p))}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase">Used</span>
                                                        <div className="w-12 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 font-black text-xs text-emerald-700 dark:text-emerald-300">
                                                            {emp.usedCasualLeave || 0}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase">Carry Fwd</span>
                                                        <input type="number" className="w-12 h-8 px-1 text-center rounded-lg border border-emerald-500/20 bg-white dark:bg-black/20 font-bold text-xs text-emerald-700 dark:text-emerald-300 outline-none focus:border-emerald-500"
                                                            value={emp.carriedForwardCasualLeave || 0}
                                                            onBlur={(e) => updateEmployeeLeave(emp._id, 'carriedForwardCasualLeave', e.target.value)}
                                                            onChange={(e) => setEmployees(prev => prev.map(p => p._id === emp._id ? {...p, carriedForwardCasualLeave: e.target.value} : p))}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-emerald-600 uppercase">Avail Bal</span>
                                                        <input type="number" className="w-14 h-8 px-1 text-center rounded-lg border border-emerald-500/40 bg-emerald-500/10 font-black text-xs text-emerald-700 dark:text-emerald-300 outline-none focus:ring-1 focus:ring-emerald-500"
                                                            value={emp.casualLeave || 0}
                                                            onBlur={(e) => updateEmployeeLeave(emp._id, 'casualLeave', e.target.value)}
                                                            onChange={(e) => setEmployees(prev => prev.map(p => p._id === emp._id ? {...p, casualLeave: e.target.value} : p))}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Medical Leave */}
                                            <td className="px-5 py-3 border-l border-[var(--border-color)]/30 bg-rose-500/5">
                                                <div className="flex items-center justify-center gap-4">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase">Rate/Mo</span>
                                                        <input type="number" className="w-12 h-8 px-1 text-center rounded-lg border border-rose-500/20 bg-white dark:bg-black/20 font-bold text-xs text-rose-700 dark:text-rose-300 outline-none focus:border-rose-500"
                                                            value={emp.monthlyMedicalLeave || 0}
                                                            onBlur={(e) => updateEmployeeLeave(emp._id, 'monthlyMedicalLeave', e.target.value)}
                                                            onChange={(e) => setEmployees(prev => prev.map(p => p._id === emp._id ? {...p, monthlyMedicalLeave: e.target.value} : p))}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase">Used</span>
                                                        <div className="w-12 h-8 flex items-center justify-center rounded-lg bg-rose-500/10 font-black text-xs text-rose-700 dark:text-rose-300">
                                                            {emp.usedMedicalLeave || 0}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase">Carry Fwd</span>
                                                        <input type="number" className="w-12 h-8 px-1 text-center rounded-lg border border-rose-500/20 bg-white dark:bg-black/20 font-bold text-xs text-rose-700 dark:text-rose-300 outline-none focus:border-rose-500"
                                                            value={emp.carriedForwardMedicalLeave || 0}
                                                            onBlur={(e) => updateEmployeeLeave(emp._id, 'carriedForwardMedicalLeave', e.target.value)}
                                                            onChange={(e) => setEmployees(prev => prev.map(p => p._id === emp._id ? {...p, carriedForwardMedicalLeave: e.target.value} : p))}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-rose-600 uppercase">Avail Bal</span>
                                                        <input type="number" className="w-14 h-8 px-1 text-center rounded-lg border border-rose-500/40 bg-rose-500/10 font-black text-xs text-rose-700 dark:text-rose-300 outline-none focus:ring-1 focus:ring-rose-500"
                                                            value={emp.medicalLeave || 0}
                                                            onBlur={(e) => updateEmployeeLeave(emp._id, 'medicalLeave', e.target.value)}
                                                            onChange={(e) => setEmployees(prev => prev.map(p => p._id === emp._id ? {...p, medicalLeave: e.target.value} : p))}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Comp Off */}
                                            <td className="px-5 py-3 border-l border-[var(--border-color)]/30 bg-blue-500/5">
                                                <div className="flex items-center justify-center">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-blue-600 uppercase">Days</span>
                                                        <input type="number" className="w-16 h-9 px-1 text-center rounded-lg border border-blue-500/40 bg-blue-500/10 font-black text-sm text-blue-700 dark:text-blue-300 outline-none focus:ring-1 focus:ring-blue-500"
                                                            value={emp.compensatoryLeave || 0}
                                                            onBlur={(e) => updateEmployeeLeave(emp._id, 'compensatoryLeave', e.target.value)}
                                                            onChange={(e) => setEmployees(prev => prev.map(p => p._id === emp._id ? {...p, compensatoryLeave: e.target.value} : p))}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            {/* LOP */}
                                            <td className="px-5 py-3 border-l border-[var(--border-color)]/30 bg-red-500/5">
                                                <div className="flex items-center justify-center">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-[9px] font-bold text-red-600 uppercase">LOP Days</span>
                                                        <input type="number" className="w-16 h-9 px-1 text-center rounded-lg border border-red-500/40 bg-red-500/10 font-black text-sm text-red-700 dark:text-red-300 outline-none focus:ring-1 focus:ring-red-500"
                                                            value={emp.lossOfPay || 0}
                                                            onBlur={(e) => updateEmployeeLeave(emp._id, 'lossOfPay', e.target.value)}
                                                            onChange={(e) => setEmployees(prev => prev.map(p => p._id === emp._id ? {...p, lossOfPay: e.target.value} : p))}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!loading && filteredEmployees.length > 0 && (
                        <div className="p-4 border-t border-[var(--border-color)] flex flex-col sm:flex-row items-center justify-between gap-4 bg-[var(--bg-secondary)]/30">
                            <div className="flex items-center gap-2 text-xs font-semibold text-[var(--text-secondary)]">
                                <span>Show</span>
                                <select 
                                    value={pageSize}
                                    onChange={(e) => setPageSize(e.target.value)}
                                    className="h-8 px-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] outline-none"
                                >
                                    <option value="10">10</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                    <option value="all">All</option>
                                </select>
                                <span>entries</span>
                            </div>

                            <div className="flex items-center gap-2 text-xs">
                                <button 
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1.5 rounded-lg border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] disabled:opacity-50 transition-colors font-bold"
                                >
                                    Previous
                                </button>
                                <span className="font-semibold text-[var(--text-secondary)]">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button 
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1.5 rounded-lg border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] disabled:opacity-50 transition-colors font-bold"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminLeaveSettings;
